// RIGLA: două clicuri pe desen și scrie cât e între ele.
//
// E o unealtă de LUCRU, nu una de desenat: nu adaugă nimic documentului, nu se
// salvează nicăieri și dispare când o stingi. Există fiindcă întrebarea „încape
// o mașină între stâlpii ăștia?" se pune uitându-te la plan, iar până acum
// singurul răspuns era să scoți geometria din bază.
//
// Se montează ca ultim copil al `<g>`-ului transformat al vizualizatorului, deci
// coordonatele ei sunt chiar metrii desenului — același loc și același truc ca
// `AnnotationLayer`, de la care împrumută și limbajul vizual: linie cu cămașă
// albă, ștampile la capete, eticheta cu halou.
//
// CIFRA NU SE MICȘOREAZĂ CU DESENUL și nu se întoarce cu el. Scrisă în metri,
// ca restul, ar fi fost ilizibilă exact la încadrarea pe tot lotul, adică
// acolo unde măsori distanțe mari; iar rotită cu planul, ar fi ajuns cu capul
// în jos la o jumătate de tură. De aceea eticheta stă într-un grup care
// anulează scara și rotația (`scale(1/pxPerM)`, `rotate(-viewTurn)`), cu
// mărimea literei în pixeli.
//
// ȘI STĂ DEOPARTE, pe normala segmentului, legată de mijloc cu un fir subțire
// (Oana, 16.09.2026). Așezată peste linie, pe o măsură scurtă plăcuța acoperea
// exact cele două capete — adică punctele pe care tocmai le ochiseși și pe care
// voiai să le corectezi. Depărtarea se socotește în pixeli, nu în metri: plăcuța
// e scrisă în pixeli, deci doar așa rămâne liberă la orice mărire.

import { DECOR } from "./glyphs";

export type Pt = { x: number; y: number };
export type Masura = { a: Pt; b: Pt };

const CULOARE = "#7c3aed";
/** Înălțimea plăcuței cu cifra, în pixeli de ecran. */
const H = 17;

/** Cât de lungă e măsura, scrisă cum se citește pe un plan: metri cu două
 *  zecimale, adică centimetri. Sub un metru trece la centimetri întregi —
 *  „0,42 m" se citește mai greu decât „42 cm" tocmai la mărimile la care
 *  întrebarea e dacă mai încape ceva. */
export function scrieLungimea(m: number): string {
  return m < 1 ? `${Math.round(m * 100)} cm` : `${m.toFixed(2)} m`;
}

function Segment({ a, b, pxPerM, viewTurn, vie }: {
  a: Pt; b: Pt; pxPerM: number; viewTurn: number; vie: boolean;
}) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const L = Math.hypot(dx, dy);
  if (!Number.isFinite(L)) return null;
  // normala unitară: pe ea stau ștampilele de capăt
  const nx = L > 1e-9 ? -dy / L : 0, ny = L > 1e-9 ? dx / L : 1;
  const tick = Math.max(0.15, 6 / pxPerM);
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
  const text = scrieLungimea(L);
  // Lățimea plăcuței din numărul de caractere: un `getBBox` ar cere o măsurare
  // în DOM la fiecare cadru al gestului, iar aici o aproximare de jumătate de
  // caracter nu se vede.
  const w = 10 + text.length * 7.2;
  // Eticheta stă LATERAL, pe normala segmentului — v. nota din capul fișierului.
  //
  // Normala, dusă în axele ECRANULUI: grupul etichetei anulează rotația
  // desenului, deci înăuntrul lui se socotește pe ecran, nu pe plan.
  const t = (viewTurn * Math.PI) / 180;
  let ux = nx * Math.cos(t) - ny * Math.sin(t);
  let uy = nx * Math.sin(t) + ny * Math.cos(t);
  // Mereu în SUSUL ecranului, ca la cotele de pe planșă: altfel plăcuța sare
  // dintr-o parte în alta după sensul în care ai tras măsura.
  if (uy > 0) { ux = -ux; uy = -uy; }
  // Depărtarea se socotește în pixeli, fiindcă în pixeli e scrisă plăcuța, și se
  // măsoară până la MARGINEA ei, nu până la mijloc: pe o măsură verticală
  // plăcuța pleacă lateral cu latura lungă spre linie, iar dusă de mijloc cu
  // jumătatea ei de înălțime ar fi călărit-o cu tot restul lățimii. `raza` e cât
  // iese plăcuța pe direcția în care e dusă; aerul de lângă linie e ștampila de
  // capăt plus câțiva pixeli.
  const raza = Math.abs(ux) * (w / 2) + Math.abs(uy) * (H / 2);
  const dus = tick * pxPerM + 4 + raza;
  // Rotunjite la sutimea de pixel: la un sfert de tură cosinusul iese 6e−17, iar
  // markup-ul s-ar umple de exponenți pentru o deplasare de nimic.
  const rot2 = (v: number) => Math.round(v * 100) / 100;
  const ox = rot2(ux * dus), oy = rot2(uy * dus);
  return (
    <g style={DECOR}>
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#ffffff" strokeWidth={5}
            vectorEffect="non-scaling-stroke" style={DECOR} />
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={CULOARE} strokeWidth={2.2}
            strokeDasharray={vie ? "6 4" : undefined}
            vectorEffect="non-scaling-stroke" style={DECOR} />
      {[a, b].map((p, i) => (
        <line key={i} x1={p.x - nx * tick} y1={p.y - ny * tick}
              x2={p.x + nx * tick} y2={p.y + ny * tick}
              stroke={CULOARE} strokeWidth={2.2}
              vectorEffect="non-scaling-stroke" style={DECOR} />
      ))}
      <g transform={`translate(${mx} ${my}) rotate(${-viewTurn}) `
                    + `scale(${1 / pxPerM})`} style={DECOR}>
        {/* Firul de la mijlocul măsurii la plăcuța dusă deoparte: fără el, pe
            un desen cu mai multe măsuri nu se mai vede a cui e cifra. Trece pe
            sub plăcuță, care e opacă. */}
        <line x1={0} y1={0} x2={ox} y2={oy} stroke={CULOARE} strokeWidth={1}
              strokeOpacity={0.5} style={DECOR} />
        <g transform={`translate(${ox} ${oy})`} style={DECOR}>
          <rect x={-w / 2} y={-H / 2} width={w} height={H} rx={3}
                fill="#ffffff" stroke={CULOARE} strokeWidth={1} style={DECOR} />
          <text x={0} y={4} fontSize={11.5} textAnchor="middle" fill={CULOARE}
                style={{ ...DECOR, fontWeight: 600,
                         fontVariantNumeric: "tabular-nums" }}>
            {text}
          </text>
        </g>
      </g>
    </g>
  );
}

export interface RiglaLayerProps {
  /** măsurile încheiate, în ordinea în care au fost trase */
  masuri: ReadonlyArray<Masura>;
  /** cea care se trage acum: al doilea capăt urmărește cursorul */
  inCurs: Masura | null;
  /** scara curentă, în pixeli pe metru — eticheta o anulează */
  pxPerM: number;
  /** cât e rotit desenul, în grade — eticheta o anulează */
  viewTurn?: number;
}

export default function RiglaLayer(
  { masuri, inCurs, pxPerM, viewTurn = 0 }: RiglaLayerProps,
) {
  if (!masuri.length && !inCurs) return null;
  return (
    <g style={DECOR} data-rigla>
      {masuri.map((m, i) => (
        <Segment key={i} a={m.a} b={m.b} pxPerM={pxPerM} viewTurn={viewTurn}
                 vie={false} />
      ))}
      {inCurs && (
        <Segment a={inCurs.a} b={inCurs.b} pxPerM={pxPerM} viewTurn={viewTurn}
                 vie />
      )}
    </g>
  );
}
